import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_CONFIG, getServiceApiUrl } from '../config/api';
import { User, AuthContextType, LoginCredentials, RegisterCredentials } from '../types/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

interface BackendLoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  email: string;
  user: User;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        
        console.log('JWT Token Payload:', payload);
        console.log('Available fields:', Object.keys(payload));
        
        if (payload.exp > Date.now() / 1000) {

          const storedEmail = localStorage.getItem('user_email');
          const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
          
          const userData = {
            id: payload.id || payload.sub,

            username: payload.sub,
            role: payload.role,
            email: storedEmail || storedUser.email || payload.email, 
            firstName: payload.firstName,
            lastName: payload.lastName,
            token: token,
          };
          
          console.log('Constructed user data:', userData);
          setUser(userData);
        } else {
          // Clear expired tokens
          localStorage.removeItem(API_CONFIG.AUTH.TOKEN_KEY);
          localStorage.removeItem('user_email'); 
          localStorage.removeItem('user'); 
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        localStorage.removeItem(API_CONFIG.AUTH.TOKEN_KEY);
        localStorage.removeItem('user_email'); 
        localStorage.removeItem('user'); 
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Starting login process...', credentials);

      const response = await fetch(getServiceApiUrl('auth', API_CONFIG.ENDPOINTS.AUTH.LOGIN), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      console.log('Login response status:', response.status);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid credentials');
        } else if (response.status === 503) {
          throw new Error('Authentication service unavailable');
        } else {
          throw new Error('Login failed');
        }
      }

      const data: BackendLoginResponse = await response.json();
      
      console.log('Login response data:', data);
      localStorage.setItem(API_CONFIG.AUTH.TOKEN_KEY, data.access_token);

      if (data?.email || data?.user?.email) {
        localStorage.setItem('user_email', data?.email || data?.user?.email);
        console.log('Stored email:', data?.email || data?.user?.email);
      }

      const payload = JSON.parse(atob(data.access_token.split('.')[1]));
      
      console.log('Login JWT Payload:', payload);
      
      const userData: User = {
        id: payload.sub || payload.id || credentials.username,
        username: credentials.username, 
        role: payload.role || 'hr',
        email: data?.email || data?.user?.email,
        firstName: data?.user?.firstName,
        lastName: data?.user?.lastName,
        fullname: data?.user?.fullname,
        token: data.access_token,
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString()
      };
      
      console.log('Final user data after login:', userData);
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      console.log('User state updated, login should be complete');
    

    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || 'Login failed. Please try again.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (credentials: RegisterCredentials): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(getServiceApiUrl('auth', API_CONFIG.ENDPOINTS.AUTH.REGISTER || '/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
      }

      const data = await response.json();
      
      if (data.access_token) {  
        localStorage.setItem(API_CONFIG.AUTH.TOKEN_KEY, data.access_token);
        
        if (data.email) {
          localStorage.setItem('user_email', data?.user?.email);
        }
        
        const payload = JSON.parse(atob(data.access_token.split('.')[1]));
        
        const userData: User = {
          id: payload.sub || payload.id,
          username: payload.sub,
          role: payload.role,
          email: data.email || payload.email,
          firstName: payload.firstName,
          lastName: payload.lastName,
          createdAt: new Date().toISOString(),
          lastLoginAt: new Date().toISOString(),
        };
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }

    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(API_CONFIG.AUTH.TOKEN_KEY);
    localStorage.removeItem(API_CONFIG.AUTH.REFRESH_TOKEN_KEY);
    localStorage.removeItem('user_email');
    localStorage.removeItem('user');
    localStorage.removeItem('user_role'); // ADDED: Remove role on logout
    setUser(null);
    setError(null);
  };

  const clearError = () => {
    setError(null);
  };

  // Role checking methods
  const getUserRole = (): string | undefined => {
    return user?.role;
  };

  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.includes(user?.role || '');
  };

  const isAdmin = (): boolean => {
    return user?.role === 'admin';
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    register,
    logout,
    clearError,
    getUserRole,
    hasRole,
    hasAnyRole,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};